import type {
  AnalysisDetail, AnalysisSummary, AnalysisTimeline, CandidateDetail, CreateJobRequest, CreateJobResponse,
  CreateExportJobRequest, Dashboard, EvidenceUrlResponse, ExportJobSummary,
  JobBatchSummary, JobSummary, Page,
  PortfolioDashboard, PortfolioHoldingsSnapshot, PortfolioAlertPage,
  CreateSimulationRequest, SimulationDefaults, SimulationDetail, SimulationSummary,
  DataHealthDaySummary, DataHealthGap, DataHealthOverview, DatasetV2Health,
} from "@signum/api-contracts";
import { demoAnalysis, demoCandidate, demoDashboard, demoJobs, demoPortfolio, demoSimulation, demoSimulationDefaults, demoTimeline } from "./demo.js";

const base = import.meta.env.VITE_API_BASE_URL ?? "/api";
const demo = import.meta.env.VITE_USE_DEMO_DATA === "true";

export class ApiClient {
  constructor(private readonly token: () => Promise<string | undefined>) {}
  datasetHealth(options: {startDate?: string; endDate?: string} = {}): Promise<DatasetV2Health> {
    return demo ? Promise.resolve({enabled:false,asOf:new Date().toISOString(),completeDates:0,
      settledWithGapsDates:0,pendingDates:0,errorDates:0,unpublishedDates:0,
      families:[],days:[],gaps:[],totalGaps:0,jobs:[]}) : this.get(`/data-health/v2?${queryString(options)}`);
  }

  dashboard(): Promise<Dashboard> { return demo ? Promise.resolve(demoDashboard) : this.get("/dashboard"); }
  portfolio(): Promise<PortfolioDashboard> { return demo ? Promise.resolve(demoPortfolio) : this.get("/portfolio"); }
  portfolioAlerts(options: ListOptions & {type: string; origin: string; publicationId?: string}): Promise<PortfolioAlertPage> {
    return demo ? Promise.resolve({items: demoPortfolio.alerts, total: demoPortfolio.alerts.length,
      page: 1, pageSize: 25, buys: demoPortfolio.totalBuyAlerts, sells: demoPortfolio.totalSellAlerts,
      inactive: demoPortfolio.totalInactiveAlerts}) : this.get(`/portfolio/alerts?${queryString(options)}`);
  }
  portfolioHoldings(asOf: string): Promise<PortfolioHoldingsSnapshot> {
    return demo ? Promise.resolve({asOfDate: asOf, holdings: demoPortfolio.holdings})
      : this.get(`/portfolio/holdings?asOf=${encodeURIComponent(asOf)}`);
  }
  simulationDefaults(): Promise<SimulationDefaults> {
    return demo ? Promise.resolve(demoSimulationDefaults) : this.get("/research/simulations/defaults");
  }
  simulations(options: ListOptions = {}): Promise<Page<SimulationSummary>> {
    const page = options.page ?? 1; const pageSize = options.pageSize ?? 25;
    return demo ? Promise.resolve({items: [], total: 0, page, pageSize})
      : this.get(`/research/simulations?${queryString(options)}`);
  }
  simulation(simulationId: string): Promise<SimulationDetail> {
    return demo ? Promise.resolve({...demoSimulation, simulationId})
      : this.get(`/research/simulations/${simulationId}`);
  }
  simulationHoldings(simulationId: string, asOf: string, thesisId?: string): Promise<PortfolioHoldingsSnapshot> {
    return demo ? Promise.resolve({asOfDate: asOf, holdings: demoSimulation.result?.holdings ?? []})
      : this.get(`/research/simulations/${simulationId}/holdings?${queryString({asOf, thesisId})}`);
  }
  createSimulation(input: CreateSimulationRequest): Promise<SimulationSummary> {
    return this.send("/research/simulations", "POST", input);
  }
  cancelSimulation(simulationId: string): Promise<SimulationSummary> {
    return this.send(`/research/simulations/${simulationId}/cancel`, "POST");
  }
  updateSimulation(simulationId: string, input: {name?: string; description?: string; archived?: boolean}): Promise<SimulationSummary> {
    return this.send(`/research/simulations/${simulationId}`, "PATCH", input);
  }
  jobs(options: ListOptions = {}): Promise<Page<JobSummary>> {
    return demo ? Promise.resolve(demoJobs) : this.get(`/jobs?${queryString(options)}`);
  }
  jobBatches(options: ListOptions = {}): Promise<Page<JobBatchSummary>> {
    const page = options.page ?? 1; const pageSize = options.pageSize ?? 25;
    return demo ? Promise.resolve({items: [], total: 0, page, pageSize}) : this.get(`/job-batches?${queryString(options)}`);
  }
  analyses(options: ListOptions = {}): Promise<Page<AnalysisSummary>> {
    const page = options.page ?? 1; const pageSize = options.pageSize ?? 25;
    return demo ? Promise.resolve({items: [demoAnalysis], total: 1, page, pageSize}) : this.get(`/analyses?${queryString(options)}`);
  }
  analysisTimeline(range: TimelineRange = 90): Promise<AnalysisTimeline> {
    if (demo) return Promise.resolve(demoTimeline(range));
    const query = typeof range === "number" ? `days=${range}`
      : `startDate=${encodeURIComponent(range.startDate)}&endDate=${encodeURIComponent(range.endDate)}`;
    return this.get(`/analysis-timeline?${query}`);
  }
  analysis(runId: string): Promise<AnalysisDetail> { return demo ? Promise.resolve(demoAnalysis) : this.get(`/analyses/${runId}`); }
  candidate(runId: string, cik: string, symbol: string): Promise<CandidateDetail> {
    return demo ? Promise.resolve(demoCandidate) : this.get(`/analyses/${runId}/candidates/${cik}/${symbol}`);
  }
  createJob(input: CreateJobRequest): Promise<CreateJobResponse> { return this.send("/jobs", "POST", input); }
  cancelJob(jobId: string): Promise<{job: JobSummary}> { return this.send(`/jobs/${jobId}/cancel`, "POST"); }
  cancelJobBatch(batchId: string): Promise<{batch: JobBatchSummary}> {
    return this.send(`/job-batches/${batchId}/cancel`, "POST");
  }
  evidenceUrl(kind: string, id: string): Promise<EvidenceUrlResponse> { return this.get(`/evidence/${kind}/${encodeURIComponent(id)}/url`); }
  async candidateExport(options: CandidateExportOptions): Promise<{blob: Blob; filename: string}> {
    const response = await this.request(`/exports/candidates.csv?${queryString(options)}`, "GET");
    const disposition = response.headers.get("Content-Disposition") ?? "";
    const filename = disposition.match(/filename="([^"]+)"/)?.[1]
      ?? `signum-candidates_${options.startDate}_${options.endDate}.csv`;
    return {blob: await response.blob(), filename};
  }
  exportJobs(includePrevious = false): Promise<{items: ExportJobSummary[]}> {
    return demo ? Promise.resolve({items: []})
      : this.get(`/export-jobs?${queryString({includePrevious})}`);
  }
  exportCapabilities():Promise<{queuedStandardExport:boolean}> {
    return demo ? Promise.resolve({queuedStandardExport:false}) : this.get("/exports/capabilities");
  }
  createExportJob(input: CreateExportJobRequest): Promise<{job: ExportJobSummary}> {
    return this.send("/export-jobs", "POST", input);
  }
  cancelExportJob(exportJobId: string): Promise<{job: ExportJobSummary}> {
    return this.send(`/export-jobs/${exportJobId}/cancel`, "POST");
  }
  exportDownload(exportJobId: string): Promise<EvidenceUrlResponse> {
    return this.get(`/export-jobs/${exportJobId}/download`);
  }
  dataHealth(range: {startDate?: string; endDate?: string} = {}): Promise<DataHealthOverview> {
    return demo ? Promise.resolve({status: "HEALTHY", strategyVersion: "2.1.1",
      certifiedStartDate: "2021-08-19", certifiedEndDate: "2026-08-26",
      lastAuditAt: new Date().toISOString(), candidateCount: 4821,
      requiredObservations: 14463, presentObservations: 14391,
      verifiedAbsentObservations: 72, unresolvedObservations: 0, coveragePct: 100,
      activeRepairJobs: 2, repairProgress: {status: "RUNNING", passNumber: 1,
        maxPasses: 3, totalDates: 1224, waitingDates: 58, activeDates: 2,
        checkedDates: 1164, certifiedDates: 67, unresolvedDates: 1097,
        systemErrorDates: 0, cancelledDates: 0, progressPct: 95.1,
        startedAt: new Date(Date.now() - 3_600_000).toISOString(),
        lastActivityAt: new Date().toISOString(), activeItems: [{jobId: "demo-repair",
          analysisDate: "2026-08-25", status: "RUNNING", currentStage: "facts",
          progressMessage: "Collecting unresolved point-in-time evidence",
          heartbeatAt: new Date().toISOString()}]}, types: [
        {factType: "listing_status", label: "Point-in-time exchange listing",
          source: "FMP + canonical prices", usedBy: "PRODUCTION_AND_PLAYGROUND",
          present: 4821, verifiedAbsent: 0, unresolved: 0, required: 4821,
          coveragePct: 100, status: "HEALTHY"},
        {factType: "operating_cash_flow", label: "Operating cash flow",
          source: "FMP financial statements", usedBy: "PRODUCTION_AND_PLAYGROUND",
          present: 4785, verifiedAbsent: 36, unresolved: 0, required: 4821,
          coveragePct: 100, status: "HEALTHY"},
        {factType: "net_income", label: "Net income",
          source: "FMP financial statements", usedBy: "PRODUCTION_AND_PLAYGROUND",
          present: 4785, verifiedAbsent: 36, unresolved: 0, required: 4821,
          coveragePct: 100, status: "HEALTHY"}], gaps: [], recentAudits: []})
      : this.get(`/data-health?${queryString(range)}`);
  }
  dataHealthGaps(options: ListOptions = {}): Promise<Page<DataHealthGap>> {
    return demo ? Promise.resolve({items: [], total: 0, page: options.page ?? 1,
      pageSize: options.pageSize ?? 25})
      : this.get(`/data-health/gaps?${queryString(options)}`);
  }
  dataHealthDays(options: ListOptions & {startDate: string; endDate: string}):
    Promise<Page<DataHealthDaySummary>> {
    return this.get(`/data-health/days?${queryString(options)}`);
  }
  dataHealthCalendar(options: {startDate: string; endDate: string}):
    Promise<{items: DataHealthDaySummary[]}> {
    return this.get(`/data-health/calendar?${queryString(options)}`);
  }
  repairDataHealth(): Promise<{status: "STARTED"}> {
    return this.send("/data-health/repair", "POST");
  }
  collectDataset(range:{startDate:string;endDate:string}):Promise<{requestId:string;status:"QUEUED"}> {
    return this.send("/data-health/v2/collect","POST",range);
  }

  private async get<T>(path: string): Promise<T> { return this.send<T>(path, "GET"); }
  private async send<T>(path: string, method: string, body?: unknown): Promise<T> {
    const response = await this.request(path, method, body);
    return response.json() as Promise<T>;
  }
  private async request(path: string, method: string, body?: unknown): Promise<Response> {
    const token = await this.token();
    const response = await fetch(`${base}${path}`, {method,
      signal: method === "GET" && path.startsWith("/portfolio") ? AbortSignal.timeout(25_000) : undefined,
      headers: {
      ...(token ? {Authorization: `Bearer ${token}`} : {}), ...(body ? {"Content-Type": "application/json"} : {}),
    }, body: body ? JSON.stringify(body) : undefined});
    if (!response.ok) {
      const payload = await response.json().catch(() => ({})) as {error?: string};
      throw new Error(payload.error ?? `Request failed (${response.status})`);
    }
    return response;
  }
}

export interface ListOptions {
  page?: number;
  pageSize?: number;
  query?: string;
  status?: string;
  sort?: string;
  direction?: "asc" | "desc";
  includePrevious?: boolean;
  startDate?: string;
  endDate?: string;
  state?: string;
  family?: string;
}

export type TimelineRange = number | {startDate: string; endDate: string};
export interface CandidateExportOptions {
  startDate: string;
  endDate: string;
  scope: "all" | "recommended";
  direction: "asc" | "desc";
}

function queryString(options: object): string {
  const parameters = new URLSearchParams();
  for (const [key, value] of Object.entries(options)) {
    if (value !== undefined && value !== "") parameters.set(key, String(value));
  }
  return parameters.toString();
}
